import { useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/shared/widgets/sheet';
import { usePopupHistory } from '@/shared/hooks/usePopupHistory';
import { cn } from '@/shared/utils/cn';
import type { SizeOptionWithEdd } from '../../domain/models/CustomizationOptions';
import { getSizeSectionLabel } from '../../utils/sizeSection.utils';
import {
  shouldShowSizeSheetEddColumn,
} from '../../utils/sizeSheetDisplay.utils';
import { CustomizationSizeEddLabel } from './CustomizationSizeEddLabel';

interface CustomizationSizeSheetProps {
  open: boolean;
  category: string;
  sizes: SizeOptionWithEdd[];
  selectedSize: SizeOptionWithEdd | null;
  nddAvailableSizes: string[] | null;
  hasPincode: boolean;
  isApplying: boolean;
  onClose: () => void;
  onSelect: (size: SizeOptionWithEdd) => void;
}

function SizeRowValue({ value, isSelected }: { value: string; isSelected: boolean }) {
  return (
    <span className="relative inline-block text-[15px] font-bold leading-6 text-[#1f3767]">
      {isSelected && (
        <Check
          className="absolute right-full top-1/2 mr-2 h-5 w-5 -translate-y-1/2 text-[#59ac59]"
          aria-hidden
        />
      )}
      {value}
    </span>
  );
}

export function CustomizationSizeSheet({
  open,
  category,
  sizes,
  selectedSize,
  nddAvailableSizes,
  hasPincode,
  isApplying,
  onClose,
  onSelect,
}: CustomizationSizeSheetProps) {
  usePopupHistory({ isOpen: open, onClose, stateKey: 'sizeSelectSheet' });
  const listRef = useRef<HTMLDivElement>(null);
  const sizeLabel = getSizeSectionLabel(category);
  const columnLabel = sizeLabel === 'Length (in inches)' ? 'Length' : 'Size';
  const selectTitle = `Select ${sizeLabel}`;
  const showEddColumn = shouldShowSizeSheetEddColumn(hasPincode, sizes);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const selectedEl = listRef.current.querySelector('[data-selected="true"]');
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="flex max-h-[75dvh] flex-col gap-0 overflow-hidden p-0"
      >
        <div className="relative shrink-0 border-b border-[#e2e2e2] px-[15px] py-3">
          <SheetTitle className="text-left text-[17px] font-normal leading-none text-black">
            {selectTitle}
          </SheetTitle>
          <button
            type="button"
            aria-label="Close size selection"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-1 text-black opacity-80 hover:opacity-100"
            onClick={onClose}
          >
            <X className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </button>
        </div>

        <div className="flex shrink-0 bg-[#ededed]">
          {showEddColumn ? (
            <>
              <div className="flex-1 border-r border-white bg-[#f5f5f5] px-2.5 py-2.5 text-center text-[15px] font-bold leading-6 text-[#808080]">
                {columnLabel}
              </div>
              <div className="flex-1 bg-[#f5f5f5] px-2.5 py-2.5 text-center text-[15px] font-bold leading-6 text-[#808080]">
                Delivery by
              </div>
            </>
          ) : (
            <div className="w-full bg-[#f5f5f5] px-2.5 py-2.5 text-center text-[15px] font-bold leading-6 text-[#808080]">
              {columnLabel}
            </div>
          )}
        </div>

        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
          {sizes.map((size) => {
            const isSelected = selectedSize?.key === size.key;

            return (
              <button
                key={size.key}
                type="button"
                data-selected={isSelected ? 'true' : 'false'}
                disabled={isApplying}
                onClick={() => onSelect(size)}
                className={cn(
                  'w-full border-b border-[#f0f0f0] text-left transition-colors',
                  isSelected ? 'bg-[rgba(89,172,89,0.15)]' : 'bg-white hover:bg-[#fafafa]'
                )}
              >
                {showEddColumn ? (
                  <div className="flex min-h-[48px] items-center">
                    <div className="flex flex-1 items-center justify-center px-2.5 py-3">
                      <SizeRowValue value={size.value} isSelected={isSelected} />
                    </div>
                    <div className="flex flex-1 items-center justify-center px-2.5 py-3">
                      <CustomizationSizeEddLabel size={size} nddAvailableSizes={nddAvailableSizes} />
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-[48px] items-center justify-center px-2.5 py-3">
                    <SizeRowValue value={size.value} isSelected={isSelected} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
